import { Button, Card, Field, SelectField } from '@homecare/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';

/**
 * Addresses and contacts, editable and removable (§6).
 *
 * The intake wizard could write one of each and nothing could change either.
 * `PATCH` and `DELETE` on both collections existed with cross-tenant coverage
 * and no control anywhere, so a mistyped street or a daughter who changed her
 * phone number was permanent.
 *
 * ## Per-row, not a whole-set PUT
 *
 * Unlike allergies and preferences, these are records rather than a set: each
 * has an id, a geocode, a geofence radius, and — for an address — EVV caller
 * ids that the field app will match against. Replacing the collection would
 * mint new ids on every save and throw away everything the geocoder and the EVV
 * slice attach to them. So the editor patches one row at a time and deletes by
 * id, which is what the endpoints are shaped for.
 *
 * ## Removal is a soft delete, and the copy does not pretend otherwise
 *
 * `DELETE` sets `deleted_at` (rule 8). The button says "Remove" rather than
 * "Delete for ever", because the record survives for the audit trail and
 * claiming otherwise would be a promise the API does not keep.
 */

const ADDRESS_TYPES = ['HOME', 'FACILITY', 'OTHER'] as const;
const SHARING_LEVELS = ['NONE', 'SCHEDULE_ONLY', 'SCHEDULE_AND_VISITS', 'FULL'] as const;

export interface AddressRow {
  id: string;
  type?: string;
  line1: string;
  line2?: string | null;
  city: string;
  state: string;
  postalCode: string;
  isPrimary?: boolean;
  entryInstructions?: string | null;
}

export interface ContactRow {
  id: string;
  firstName: string;
  lastName: string;
  relationship?: string | null;
  phone?: string | null;
  email?: string | null;
  isEmergencyContact?: boolean;
  sharingLevel?: string;
}

const LABEL = (value: string): string =>
  value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ');

/** One line, the way an envelope reads it. */
export const formatAddress = (address: AddressRow): string =>
  [address.line1, address.line2, `${address.city}, ${address.state} ${address.postalCode}`]
    .filter(Boolean)
    .join(', ');

// ---------------------------------------------------------------------------

export function AddressesCard({
  clientId,
  addresses,
  canEdit,
}: {
  clientId: string;
  addresses: AddressRow[] | undefined;
  canEdit: boolean;
}): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<Partial<AddressRow>>({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const rows = addresses ?? [];
  const refresh = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['clients', 'detail', clientId] });

  const begin = (row?: AddressRow): void => {
    setDraft(
      row ?? {
        type: 'HOME',
        line1: '',
        city: '',
        state: '',
        postalCode: '',
        isPrimary: rows.length === 0,
      },
    );
    setFailure(null);
    setEditing(row?.id ?? 'new');
  };

  const save = async (): Promise<void> => {
    setBusy(true);
    setFailure(null);

    try {
      const body = {
        type: draft.type ?? 'HOME',
        line1: (draft.line1 ?? '').trim(),
        line2: draft.line2?.trim() || null,
        city: (draft.city ?? '').trim(),
        state: (draft.state ?? '').trim().toUpperCase(),
        postalCode: (draft.postalCode ?? '').trim(),
        entryInstructions: draft.entryInstructions?.trim() || null,
        isPrimary: draft.isPrimary ?? false,
      };

      if (editing === 'new') {
        await api.send('POST', `/v1/clients/${clientId}/addresses`, body);
      } else {
        await api.send('PATCH', `/v1/clients/${clientId}/addresses/${editing}`, body);
      }

      await refresh();
      setEditing(null);
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string): Promise<void> => {
    setBusy(true);
    setFailure(null);

    try {
      await api.send('DELETE', `/v1/clients/${clientId}/addresses/${id}`);
      await refresh();
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="text-ink text-lg font-bold">Addresses</h2>
        {canEdit && editing === null && (
          <Button variant="secondary" size="sm" onClick={() => begin()}>
            Add an address
          </Button>
        )}
      </div>

      <ErrorNote error={failure} nextStep="Check the address and try again." />

      {editing !== null && (
        <div
          className="border-line bg-surface-2 mt-4 grid gap-3 rounded-md border p-4"
          data-testid="address-editor"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField
              label="Address type"
              value={draft.type ?? 'HOME'}
              onChange={(event) => setDraft((was) => ({ ...was, type: event.target.value }))}
            >
              {ADDRESS_TYPES.map((type) => (
                <option key={type} value={type}>
                  {LABEL(type)}
                </option>
              ))}
            </SelectField>

            <Field
              label="Street address"
              value={draft.line1 ?? ''}
              onChange={(event) => setDraft((was) => ({ ...was, line1: event.target.value }))}
            />
            <Field
              label="City"
              value={draft.city ?? ''}
              onChange={(event) => setDraft((was) => ({ ...was, city: event.target.value }))}
            />
            <Field
              label="State"
              maxLength={2}
              value={draft.state ?? ''}
              onChange={(event) => setDraft((was) => ({ ...was, state: event.target.value }))}
            />
            <Field
              label="ZIP"
              value={draft.postalCode ?? ''}
              onChange={(event) => setDraft((was) => ({ ...was, postalCode: event.target.value }))}
            />
            <Field
              label="Entry instructions"
              hint="Lockbox code, gate code, which door. The field app shows this."
              value={draft.entryInstructions ?? ''}
              onChange={(event) =>
                setDraft((was) => ({ ...was, entryInstructions: event.target.value }))
              }
            />
          </div>

          <label className="text-ink-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.isPrimary ?? false}
              onChange={(event) => setDraft((was) => ({ ...was, isPrimary: event.target.checked }))}
            />
            {/* The API keeps exactly one primary, so ticking this untick the
                other — said here so the result is not a surprise. */}
            Primary address — visits go here unless told otherwise
          </label>

          <div className="flex items-center gap-2">
            <Button
              onClick={() => void save()}
              pending={busy}
              {...((draft.line1 ?? '').trim() && (draft.city ?? '').trim()
                ? {}
                : ({ disabled: true, disabledReason: 'A street and a city at least.' } as const))}
            >
              Save address
            </Button>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {rows.length === 0 && editing === null ? (
        <p className="text-ink-3 mt-3 text-sm">None recorded.</p>
      ) : (
        <ul className="mt-3 grid gap-2">
          {rows.map((address) => (
            <li key={address.id} className="flex flex-wrap items-start justify-between gap-3">
              <span className="text-ink-2 text-sm">
                {formatAddress(address)}
                {address.isPrimary && <span className="text-ink-3"> · primary</span>}
                {address.entryInstructions && (
                  <span className="text-ink-3 block text-xs">{address.entryInstructions}</span>
                )}
              </span>

              {canEdit && editing === null && (
                <span className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => begin(address)}>
                    Edit
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => void remove(address.id)}>
                    Remove
                  </Button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

export function ContactsCard({
  clientId,
  contacts,
  canEdit,
}: {
  clientId: string;
  contacts: ContactRow[] | undefined;
  canEdit: boolean;
}): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<Partial<ContactRow>>({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const rows = contacts ?? [];
  const refresh = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['clients', 'detail', clientId] });

  const begin = (row?: ContactRow): void => {
    setDraft(
      row ?? { firstName: '', lastName: '', relationship: '', sharingLevel: 'SCHEDULE_ONLY' },
    );
    setFailure(null);
    setEditing(row?.id ?? 'new');
  };

  const save = async (): Promise<void> => {
    setBusy(true);
    setFailure(null);

    try {
      const body = {
        firstName: (draft.firstName ?? '').trim(),
        lastName: (draft.lastName ?? '').trim(),
        relationship: (draft.relationship ?? '').trim(),
        phone: draft.phone?.trim() || null,
        email: draft.email?.trim() || null,
        isEmergencyContact: draft.isEmergencyContact ?? false,
        sharingLevel: draft.sharingLevel ?? 'SCHEDULE_ONLY',
      };

      if (editing === 'new') {
        await api.send('POST', `/v1/clients/${clientId}/contacts`, body);
      } else {
        await api.send('PATCH', `/v1/clients/${clientId}/contacts/${editing}`, body);
      }

      await refresh();
      setEditing(null);
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string): Promise<void> => {
    setBusy(true);
    setFailure(null);

    try {
      await api.send('DELETE', `/v1/clients/${clientId}/contacts/${id}`);
      await refresh();
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="text-ink text-lg font-bold">Contacts</h2>
        {canEdit && editing === null && (
          <Button variant="secondary" size="sm" onClick={() => begin()}>
            Add a contact
          </Button>
        )}
      </div>

      <ErrorNote error={failure} nextStep="Check the details and try again." />

      {editing !== null && (
        <div
          className="border-line bg-surface-2 mt-4 grid gap-3 rounded-md border p-4"
          data-testid="contact-editor"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Contact first name"
              value={draft.firstName ?? ''}
              onChange={(event) => setDraft((was) => ({ ...was, firstName: event.target.value }))}
            />
            <Field
              label="Contact last name"
              value={draft.lastName ?? ''}
              onChange={(event) => setDraft((was) => ({ ...was, lastName: event.target.value }))}
            />
            <Field
              label="Relationship"
              hint="Daughter, neighbour, case manager."
              value={draft.relationship ?? ''}
              onChange={(event) =>
                setDraft((was) => ({ ...was, relationship: event.target.value }))
              }
            />
            <Field
              label="Phone"
              value={draft.phone ?? ''}
              onChange={(event) => setDraft((was) => ({ ...was, phone: event.target.value }))}
            />
            <Field
              label="Email"
              type="email"
              value={draft.email ?? ''}
              onChange={(event) => setDraft((was) => ({ ...was, email: event.target.value }))}
            />
            <SelectField
              label="What they may see"
              // The family portal reads this. It is not cosmetic: it decides
              // how much of somebody's care this person is shown.
              hint="Governs the family portal when it lands."
              value={draft.sharingLevel ?? 'SCHEDULE_ONLY'}
              onChange={(event) =>
                setDraft((was) => ({ ...was, sharingLevel: event.target.value }))
              }
            >
              {SHARING_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {LABEL(level)}
                </option>
              ))}
            </SelectField>
          </div>

          <label className="text-ink-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.isEmergencyContact ?? false}
              onChange={(event) =>
                setDraft((was) => ({ ...was, isEmergencyContact: event.target.checked }))
              }
            />
            Emergency contact
          </label>

          <div className="flex items-center gap-2">
            <Button
              onClick={() => void save()}
              pending={busy}
              {...((draft.firstName ?? '').trim() &&
              (draft.lastName ?? '').trim() &&
              (draft.relationship ?? '').trim()
                ? {}
                : ({
                    disabled: true,
                    // The contract requires a relationship, and a contact with
                    // a name and no relationship is worse than none — nobody
                    // knows whether to ring them.
                    disabledReason: 'A name and how they are related.',
                  } as const))}
            >
              Save contact
            </Button>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {rows.length === 0 && editing === null ? (
        <p className="text-ink-3 mt-3 text-sm">None recorded.</p>
      ) : (
        <ul className="mt-3 grid gap-2">
          {rows.map((contact) => (
            <li key={contact.id} className="flex flex-wrap items-start justify-between gap-3">
              <span className="text-sm">
                <span className="text-ink font-medium">
                  {contact.firstName} {contact.lastName}
                </span>
                {contact.relationship && (
                  <span className="text-ink-3"> · {contact.relationship}</span>
                )}
                {contact.phone && (
                  <span className="text-ink-2 block font-mono">{contact.phone}</span>
                )}
              </span>

              {canEdit && editing === null && (
                <span className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => begin(contact)}>
                    Edit
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => void remove(contact.id)}>
                    Remove
                  </Button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
